import { Controller } from 'stimulus'
import {
  donut, gauge, pairedStacks, hBars, renderTable,
  fmtNum, SLOTS
} from '../lib/metrics/charts'

// Testing types beyond the fixed slot count fold into "Other" — slot hues
// are assigned in rank order once per load, never cycled.
const OTHER = 'Other'
const OTHER_COLOR = 'var(--m-other)'
const LINE_COLOR = 'var(--m-ink2)'
const DONUT_SIZE = 128
const PROJECTS_PAGE = 25
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Caption for a sorted list of ISO month starts: "Jan–Aug", "Aug",
// "Nov 2025–Feb 2026", or a count when the months are not consecutive.
function monthSpan(isos) {
  if (!isos.length) return ''
  const index = (iso) => { const [y, m] = iso.split('-').map(Number); return y * 12 + (m - 1) }
  const first = index(isos[0])
  const last = index(isos[isos.length - 1])
  if (last - first + 1 !== isos.length) return `${isos.length} months`
  const sameYear = Math.floor(first / 12) === Math.floor(last / 12)
  const label = i => `${MONTH_ABBR[i % 12]}${sameYear ? '' : ` ${Math.floor(i / 12)}`}`
  return first === last ? label(first) : `${label(first)}–${label(last)}`
}

// Drives the Technician Dashboard tab (docs/TECHNICIAN_DASHBOARD_BRIEF.md).
// Lazy-loads on first activation (metrics:technician-shown). The two
// testing-type cards (donut + monthly chart) render together so both use
// one type→color mapping.
export default class extends Controller {
  static targets = [
    'from', 'to', 'location',
    'gaugeForecast', 'gaugeActual', 'locations',
    'typeDonut', 'techBars',
    'monthlyChart', 'monthlyLegend',
    'projects', 'projectsSub', 'projectsMore'
  ];

  connect() {
    this.initFromUrl()
    this.onShow = () => this.load()
    window.addEventListener('metrics:technician-shown', this.onShow)
    // Direct ?tab=technician navigation: the outer controller activates this
    // panel before our listener registers — load off the surviving class.
    if (this.element.classList.contains('active')) this.load()
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.element.offsetParent !== null) this.load()
      }, 160)
    }
    window.addEventListener('resize', this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener('metrics:technician-shown', this.onShow)
    window.removeEventListener('resize', this.resizeHandler)
  }

  initFromUrl() {
    const url = new URLSearchParams(window.location.search)
    if (url.get('tab') !== 'technician') return
    if (url.get('from')) this.fromTarget.value = url.get('from')
    if (url.get('to')) this.toTarget.value = url.get('to')
    if (url.get('location')) this.locationTarget.value = url.get('location')
  }

  load() {
    this.loadSeq = (this.loadSeq || 0) + 1
    this.projectRows = []
    const qs = this.queryString()
    this.fetchJson(`${this.data.get('summaryUrl')}?${qs}`, data => this.renderSummary(data))
    this.fetchProjects(0)
    // hours + monthly share the testing-type palette, so they render together
    const seq = this.loadSeq
    Promise.all([
      this.fetchData(`${this.data.get('hoursUrl')}?${qs}`),
      this.fetchData(`${this.data.get('monthlyUrl')}?${qs}`)
    ]).then(([hours, monthly]) => {
      if (seq !== this.loadSeq || !hours || !monthly) return
      const palette = this.typePalette(hours, monthly)
      this.renderHours(hours, palette)
      this.renderMonthly(monthly, palette)
    })
  }

  queryString() {
    const p = new URLSearchParams()
    if (this.fromTarget.value) p.set('from', this.fromTarget.value)
    if (this.toTarget.value) p.set('to', this.toTarget.value)
    if (this.locationTarget.value) p.set('location', this.locationTarget.value)
    return p.toString()
  }

  // What "+ Save current view" persists for this tab (see metrics#saveView)
  viewParams() {
    const params = { tab: 'technician' }
    if (this.fromTarget.value) params.from = this.fromTarget.value
    if (this.toTarget.value) params.to = this.toTarget.value
    if (this.locationTarget.value) params.location = this.locationTarget.value
    return params
  }

  fetchJson(url, onData, guarded = true) {
    const seq = this.loadSeq || 0
    this.fetchData(url).then((data) => {
      if (!data) return
      if (guarded && seq !== (this.loadSeq || 0)) return
      onData(data)
    })
  }

  fetchData(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
      .catch(() => null)
  }

  // Fixed slot order by total hours (actual donut + monthly stacks); ranks
  // beyond the slot count map to Other.
  typePalette(hours, monthly) {
    const totals = {}
    hours.by_type.forEach((r) => { totals[r.label] = (totals[r.label] || 0) + r.hours })
    monthly.types.forEach((t) => {
      const sum = arr => (arr || []).reduce((a, v) => a + v, 0)
      totals[t] = (totals[t] || 0) + sum(monthly.actual[t]) + sum(monthly.forecast[t])
    })
    const ranked = Object.keys(totals).sort((x, y) => totals[y] - totals[x])
    const colors = {}
    ranked.forEach((t, i) => { colors[t] = i < SLOTS.length ? SLOTS[i] : OTHER_COLOR })
    return { ranked: ranked.slice(0, SLOTS.length), colors, hasOther: ranked.length > SLOTS.length }
  }

  foldRows(rows, palette) {
    const kept = []
    let other = 0
    rows.forEach((r) => {
      if (palette.colors[r.label] === OTHER_COLOR) other += r.value
      else kept.push({ ...r, color: palette.colors[r.label] })
    })
    if (other > 0) kept.push({ label: OTHER, value: other, color: OTHER_COLOR })
    return kept
  }

  renderSummary(data) {
    // both gauges: value-added hours (less PTO and Admin jobs) of available
    // hours less the same PTO; each nets its own fact table
    gauge(this.gaugeForecastTarget, data.forecast_pct, {
      sub: `${fmtNum(data.forecast_net_hours)} of ${fmtNum(data.forecast_available_hours)} h`
    })
    // the actuals gauge covers only the months with imported actuals, so
    // its caption names them
    const months = monthSpan(data.actual_months || [])
    gauge(this.gaugeActualTarget, data.actual_pct, {
      sub: `${fmtNum(data.actual_net_hours)} of ${fmtNum(data.actual_available_hours)} h${months ? ` · ${months}` : ''}`,
      empty: months ? undefined : 'No actual hours imported in this range.'
    })
    donut(
      this.locationsTarget,
      data.by_location.map((r, i) => ({ label: r.label, value: r.count, color: SLOTS[i % SLOTS.length] })),
      { centerSub: 'techs', size: DONUT_SIZE }
    )
  }

  renderHours(data, palette) {
    donut(
      this.typeDonutTarget,
      this.foldRows(data.by_type.map(r => ({ label: r.label, value: r.hours })), palette),
      { centerSub: 'hours', size: DONUT_SIZE }
    )
    hBars(
      this.techBarsTarget,
      data.by_technician.map(r => ({ label: r.label, value: r.hours, color: 'var(--m-s1)' }))
    )
  }

  renderMonthly(data, palette) {
    const series = clusterKey => palette.ranked
      .map(t => ({ label: t, color: palette.colors[t], values: data[clusterKey][t] || [] }))
      .concat(palette.hasOther ? [{
        label: OTHER,
        color: OTHER_COLOR,
        values: data.months.map((_, i) => Object.keys(data[clusterKey])
          .filter(t => palette.colors[t] === OTHER_COLOR)
          .reduce((a, t) => a + (data[clusterKey][t][i] || 0), 0))
      }] : [])
    const lines = [{ label: 'Available hours', values: data.available, color: LINE_COLOR }]
    pairedStacks(this.monthlyChartTarget, {
      groups: data.months,
      clusters: [
        { key: 'actual', label: 'Actual', series: series('actual') },
        { key: 'forecast', label: 'Forecast', series: series('forecast'), faded: true }
      ],
      lines
    })
    const legend = this.monthlyLegendTarget
    legend.innerHTML = ''
    const chip = (label, color, extraClass) => {
      const key = document.createElement('span')
      key.className = 'm-key'
      const sw = document.createElement('span')
      sw.className = `m-swatch${extraClass ? ` ${extraClass}` : ''}`
      // backgroundColor (not the shorthand) so the dashed modifier's
      // background-image survives
      sw.style.backgroundColor = color
      key.appendChild(sw)
      key.appendChild(document.createTextNode(label))
      legend.appendChild(key)
    }
    palette.ranked.forEach(t => chip(t, palette.colors[t]))
    if (palette.hasOther) chip(OTHER, OTHER_COLOR)
    chip('Forecast (faded bar)', 'var(--m-grid)')
    lines.forEach(l => chip(l.label, l.color, l.dash ? 'm-swatch-dash' : 'm-swatch-line'))
  }

  fetchProjects(offset) {
    this.projectsMoreTarget.disabled = true
    this.fetchJson(
      `${this.data.get('projectsUrl')}?${this.queryString()}&per_page=${PROJECTS_PAGE}&offset=${offset}`,
      data => this.renderProjects(data)
    )
  }

  seeMore() {
    this.fetchProjects(this.projectRows.length)
  }

  renderProjects(data) {
    // append only the page we're actually waiting on (a double-click or a
    // cached duplicate would otherwise repeat rows)
    if (data.offset !== this.projectRows.length) return
    this.projectRows.push(...data.rows)
    const fmt = iso => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US') : '—')
    const clip = (s, n = 90) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')
    renderTable(
      this.projectsTarget,
      [
        { label: 'Technician' }, { label: 'Start' }, { label: 'End' },
        { label: 'Testing type' }, { label: 'Job ID' }, { label: 'Job name' },
        { label: 'Latest comment' }
      ],
      this.projectRows.map(r => [
        r.technician, fmt(r.start_date), fmt(r.end_date),
        r.testing_type, r.display_id, r.job_name, clip(r.comment)
      ]),
      (ri) => { window.location.href = `/jobs/${this.projectRows[ri].job_id}` }
    )
    const remaining = data.total - this.projectRows.length
    const shown = `Showing ${fmtNum(this.projectRows.length)} of ${fmtNum(data.total)} schedules overlapping this range`
    this.projectsSubTarget.textContent = `${shown} · click a row to open the job`
    this.projectsMoreTarget.disabled = false
    this.projectsMoreTarget.style.display = remaining > 0 ? '' : 'none'
    this.projectsMoreTarget.textContent = remaining > 0
      ? `See more (${fmtNum(remaining)} remaining)`
      : 'See more'
  }
}
